/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import {allProducts} from "abportal/utils/PortalProducts";
import {AbHttpErrorPage, KnownErrorType} from "abreact/misc/AbHttpErrorPage";
import {bootstrapPortal} from "absdk/preliminary/framework/app/AbPortalBootstrap";
import {RemoteError} from "abweb/dto/exception/RemoteError";
import {ProductInfo} from "abweb/dto/product/ProductInfo";
import {AbWebServices} from "abweb/service/AbWebServices";
import {AppLoaderProps} from "abweb/service/app/AppLoaderProps";
import {FailedToFetchError} from "abweb/service/utils/rpc/FailedToFetchError";
import {StatusCode} from "abweb/service/utils/rpc/FetchUtils";
import {ClassUtils} from "abweb/utils/ClassUtils";
import {PromiseUtils} from "abweb/utils/PromiseUtils";
import * as PlatformResources from "app/generated/locale/PlatformResources";
import {PlatformPortalInfo} from "plugin/services/PlatformPortalInfo";
import React from "react";
import "./CssApp";

async function checkForStartup(alProps: AppLoaderProps) {
  await AbWebServices.localeService.loadResourceBundles(["webapp/app/assets/locale/{locale}/platform.json"], []);
  await checkForAG(alProps);
  await checkForAIDPServices(alProps);
}

async function checkForAG(alProps: AppLoaderProps) {
  const busyTimeout = setTimeout(() => {
    alProps.updateBusyMsg(PlatformResources.startup_waitingForAG());
  }, 1000);
  try {
    await PromiseUtils.poll(
      () => AbWebServices.productService.getProductInfoProbe(`/../${allProducts.AG.contextRoot}`),
      `${allProducts.AG.plainTextName} productInfo`,
      6000,
      5 * 60 * 1000 // 5 minutes
    );
  } catch (e) {
    handleProductProbeError(e, allProducts.AG.plainTextName, alProps);
  } finally {
    clearTimeout(busyTimeout);
  }
}

async function checkForAIDPServices(alProps: AppLoaderProps) {
  const busyTimeout = setTimeout(
    () => alProps.updateBusyMsg(PlatformResources.startup_waitingForApplicationServices()),
    1000
  );
  let productName = allProducts.CAFE.plainTextName;
  try {
    await checkForCafe();
    productName = allProducts.MHUB.plainTextName;
    await checkForMHub();
  } catch (e) {
    handleProductProbeError(e, productName, alProps);
  } finally {
    clearTimeout(busyTimeout);
  }
}

async function checkForCafe() {
  await PromiseUtils.poll(
    AbWebServices.productService.getProductInfoProbe,
    `${allProducts.CAFE.plainTextName} productInfo`,
    500,
    60 * 1000 // 1 minute
  );
}

async function checkForMHub() {
  await PromiseUtils.poll(
    () => AbWebServices.productService.getProductInfoProbe(`/../${allProducts.MHUB.contextRoot}`),
    `${allProducts.MHUB.plainTextName} productInfo`,
    500,
    60 * 1000 // 1 minute
  );
}

function handleProductProbeError(error: unknown, productName: string, alProps: AppLoaderProps) {
  if (ClassUtils.isInstanceOf(error, RemoteError) &&
      error.response?.status &&
      [StatusCode.BAD_GATEWAY, StatusCode.SERVICE_UNAVAILABLE, StatusCode.GATEWAY_TIMEOUT].includes(error.response.status)
  ) {
    alProps.setErrorContent(
      <AbHttpErrorPage
        errorInfo={{
          statusCode: error.response.status,
          title: PlatformResources.startup_unavailableServer_title(), // this one doesn't show "503"
          message: PlatformResources.startup_unavailableServer_message(AbWebServices.sessionData.productInfo.officialName),
          body: <div>{PlatformResources.startup_unavailableServer_body()}</div>
        }}
      />
    );
    PromiseUtils.throwAlreadyHandled(error);
  } else if (ClassUtils.isInstanceOf(error, FailedToFetchError)) {
    alProps.setErrorContent(
      <AbHttpErrorPage
        errorInfo={{
          knownErrorType: KnownErrorType.SERVICE_UNAVAILABLE,
          overrideMessage: PlatformResources.startup_unavailableServer_message(AbWebServices.sessionData.productInfo.officialName),
          overrideBody: <div>{PlatformResources.startup_unavailableServer_body()}</div>
        }}
      />
    );
    PromiseUtils.throwAlreadyHandled(error);
  } else {
    throw error;
  }
}

async function getProductInfo(): Promise<ProductInfo> {
  await PlatformPortalInfo.initialize();
  const productInfo = ProductInfo.createProductInfo(
    null!, // allowGuestAccess
    PlatformPortalInfo.platform.description, // buildID
    null!, // date
    null, // helpModule
    null, // items
    PlatformPortalInfo.platform.name, // name
    null, // productVersion
    null!, // usesUserAuthentication
    PlatformPortalInfo.platform.version); // version
  if (PlatformPortalInfo.platform.officialName) {
    productInfo.officialName = PlatformPortalInfo.platform.officialName;
  }
  return productInfo;
}

bootstrapPortal(() => import(/* webpackChunkName: "PortalApplication" */ "../portal/Portal"), {
  productInfoImportFn: getProductInfo,
  preAuthHook: checkForStartup
});
